import { AppstoreAddOutlined, DeleteOutlined, DownOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu, Space } from 'antd';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type PayGatePaymentModuleSpecsFragment } from '../../../../api/fragments/PayGatePaymentModuleSpecs';
import { type PermissionValues } from '../../../../utilities/hasPermissions';
import useDeleteModule from '../useDeleteModule';
import { usePayGatePaymentSettingsDrawer } from './PayGatePaymentSettingsDrawer';
import { useUpdatePayGatePaymentModuleDrawer } from './UpdatePayGatePaymentModuleDrawer';

export type PayGatePaymentModuleActionsProps = {
    module: PayGatePaymentModuleSpecsFragment;
    permissions: Pick<PermissionValues, 'hasDeletePermission' | 'hasUpdatePermission'>;
};

const PayGatePaymentModuleActions = ({ module, permissions }: PayGatePaymentModuleActionsProps) => {
    const { t } = useTranslation(['paygatePaymentModuleDetails', 'moduleDetails']);
    const deleteModule = useDeleteModule(module.id);
    const addPayGatePaymentSettingsDrawer = usePayGatePaymentSettingsDrawer(module.id);

    const initialValues = useMemo(() => pick(['displayName'], module), [module]);
    const updatePayGatePaymentModuleDrawer = useUpdatePayGatePaymentModuleDrawer(module.id, initialValues);
    const { hasUpdatePermission, hasDeletePermission } = permissions;

    const items = [
        hasUpdatePermission && {
            label: t('paygatePaymentModuleDetails:mainActionButtons.updateSpecs'),
            key: 'updateSpecs',
            icon: <EditOutlined />,
            onClick: updatePayGatePaymentModuleDrawer.open,
        },
        hasUpdatePermission && {
            label: t('paygatePaymentModuleDetails:mainActionButtons.addSetting'),
            key: 'addSetting',
            icon: <AppstoreAddOutlined />,
            onClick: addPayGatePaymentSettingsDrawer.open,
        },
        hasDeletePermission && {
            label: t('paygatePaymentModuleDetails:mainActionButtons.delete'),
            key: 'delete',
            icon: <DeleteOutlined />,
            onClick: deleteModule,
            danger: true,
        },
    ];

    return (
        <>
            <Dropdown overlay={<Menu items={items} />}>
                <Button type="primary">
                    <Space>
                        <span>{t('paygatePaymentModuleDetails:mainActionButtons.actions')}</span>
                        <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
            {addPayGatePaymentSettingsDrawer.render()}
            {updatePayGatePaymentModuleDrawer.render()}
        </>
    );
};

export default PayGatePaymentModuleActions;
